import { Amplify } from 'aws-amplify'
import { env } from './env'
import {
  getCognitoHostedDomain,
  getOAuthRedirectSignIn,
  getOAuthRedirectSignOut,
  isCognitoConfigured,
} from './cognito'
import { isNativeApp } from '../utils/platform'

/**
 * Resolves once native auth-token storage is ready (migration complete).
 * AuthProvider awaits this before its first session restore so the very first
 * `getAuthUser()` reads migrated tokens instead of empty Preferences.
 */
let authStorageReadyPromise: Promise<void> = Promise.resolve()

export function whenAuthStorageReady(): Promise<void> {
  return authStorageReadyPromise
}

/**
 * Swap Amplify's token store to native storage (SecureStorage: backup-excluded
 * files on iOS, Capacitor Preferences on Android) so the Cognito session survives
 * restarts / cache clears. No-op on web (keeps localStorage).
 */
async function installNativeTokenStorage(): Promise<void> {
  if (!isNativeApp()) return
  const [
    { cognitoUserPoolsTokenProvider },
    {
      capacitorTokenStorage,
      migrateLocalStorageTokensToPreferences,
      scrubLocalStorageCognitoKeysIfSafe,
      setKnownCognitoClientId,
    },
    { sweepLegacySecureKeys },
  ] = await Promise.all([
    import('aws-amplify/auth/cognito'),
    import('./amplify-native-storage'),
    import('../services/secure-storage'),
  ])
  // iOS: move any legacy NSUserDefaults tokens into the backup-excluded secure
  // store (copy → verify → delete; a legacy entry WINS over a stale secure copy,
  // which is what makes downgrade → re-upgrade correct). Runs once per process;
  // a failure leaves the legacy copy in place and the session intact.
  await sweepLegacySecureKeys().catch(() => false)
  setKnownCognitoClientId(env.cognitoClientId)
  // Copy into Preferences first, THEN point Amplify at Preferences, THEN scrub
  // WebView localStorage (only if Preferences actually holds the session).
  // Scrubbing before setKeyValueStorage / while Preferences was empty was the
  // #232 Android failure mode → Amplify AppSync "No federated jwt".
  await migrateLocalStorageTokensToPreferences()
  cognitoUserPoolsTokenProvider.setKeyValueStorage(capacitorTokenStorage)
  await scrubLocalStorageCognitoKeysIfSafe()
}

/** Configure AWS Amplify — Cognito User Pools (ap-southeast-2) + optional AppSync */
export function configureAmplify(): void {
  if (!isCognitoConfigured()) {
    console.warn('[auth] Cognito env vars missing — set VITE_COGNITO_* in frontend/.env.local')
    return
  }

  if (isNativeApp()) {
    authStorageReadyPromise = installNativeTokenStorage().catch((err) => {
      console.warn('[auth] Native token storage setup failed; using default storage', err)
    })
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: env.cognitoUserPoolId,
        userPoolClientId: env.cognitoClientId,
        loginWith: {
          email: true,
          oauth: {
            domain: getCognitoHostedDomain(),
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [getOAuthRedirectSignIn()],
            redirectSignOut: [getOAuthRedirectSignOut()],
            responseType: 'code',
          },
        },
      },
    },
    ...(env.appsyncGraphqlUrl
      ? {
          API: {
            GraphQL: {
              endpoint: env.appsyncGraphqlUrl,
              region: env.awsRegion,
              defaultAuthMode: 'userPool' as const,
            },
          },
        }
      : {}),
  })
}
